import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, 
  AlertTriangle, 
  Loader, 
  User, 
  Phone, 
  Car, 
  MapPin, 
  Clock, 
  Star,
  Navigation,
  DollarSign,
  Route,
  Play,
  Users,
  Shield,
  BookOpen,
  X,
  FileText,
  Heart,
  Download,
  Printer,
  ImageIcon
} from 'lucide-react';
import { animations } from '../animations';
import {
  Header,
  Body,
  SummaryCard,
  LinkList,
  DriverList,
  StatusIndicator,
  ButtonGroup,
  Button,
  LoadingOverlay,
  EmergencyDetailsCard,
  PriceTag,
  DistanceIndicator
} from '../StyledComponents';
import { useFirstAidGuide } from '../../../hooks/useFirstAidGuide';
import { ProfessionalLoader } from '../components/ProfessionalLoader';

// First Aid Modal Component
const FirstAidModal = ({ isOpen, onClose, guideId }) => {
  const { guide, loading, error } = useFirstAidGuide(guideId, isOpen);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '0.5rem'
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 30 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          style={{
            background: 'white',
            borderRadius: '1rem',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '95vh',
            overflow: 'hidden',
            boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Compact Header */}
          <div style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
            padding: '1.25rem',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
              pointerEvents: 'none'
            }}></div>
            
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '0.75rem',
                right: '0.75rem',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              <X size={16} style={{ color: 'white' }} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Heart size={20} style={{ color: 'white' }} />
              </div>
              <div>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: '1.25rem', 
                  fontWeight: '700',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  🚑 First Aid Guide
                </h2>
                <p style={{ 
                  margin: '0', 
                  opacity: 0.9, 
                  fontSize: '0.75rem', 
                  color: 'white'
                }}>
                  Emergency first aid instructions
                </p>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div style={{
            padding: '1.25rem',
            overflowY: 'auto',
            flex: 1,
            minHeight: 0
          }}>
            {/* Loading/Error/Content */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Loader size={32} className="spinning" />
                <p>Loading guide...</p>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', color: '#dc2626', padding: '2rem' }}>{error}</div>
            ) : guide ? (
              <>
                {/* Title Section */}
                <div style={{
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  borderRadius: '0.75rem',
                  padding: '1rem',
                  marginBottom: '1.25rem',
                  border: '1px solid #f59e0b'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AlertTriangle size={20} style={{ color: '#d97706' }} />
                    <h3 style={{ 
                      margin: 0, 
                      color: '#92400e', 
                      fontSize: '1rem',
                      fontWeight: '600'
                    }}>
                      {guide.title}
                    </h3>
                  </div>
                </div>

                {/* Steps */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {guide.steps && guide.steps.length > 0 ? guide.steps.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -15 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.08 }}
                      style={{
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        borderRadius: '0.75rem',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        background: `linear-gradient(135deg, ${
                          index < 2 ? '#dc2626' : index < 4 ? '#f59e0b' : '#059669'
                        } 0%, ${
                          index < 2 ? '#ef4444' : index < 4 ? '#fbbf24' : '#10b981'
                        } 100%)`
                      }}></div>

                      {/* Step Header */}
                      <div style={{ 
                        padding: '1rem 1rem 0.75rem 1rem',
                        display: 'flex', 
                        alignItems: 'center',
                        gap: '0.75rem' 
                      }}>
                        <div style={{
                          background: `linear-gradient(135deg, ${
                            index < 2 ? '#fef2f2' : index < 4 ? '#fffbeb' : '#f0fdf4'
                          } 0%, ${
                            index < 2 ? '#fee2e2' : index < 4 ? '#fef3c7' : '#dcfce7'
                          } 100%)`,
                          color: index < 2 ? '#dc2626' : index < 4 ? '#d97706' : '#059669',
                          borderRadius: '50%',
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          flexShrink: 0,
                          border: `2px solid ${
                            index < 2 ? '#fecaca' : index < 4 ? '#fed7aa' : '#bbf7d0'
                          }`
                        }}>
                          {item.label || item.step}
                        </div>
                        
                        {/* Step Title (if text content) */}
                        {(!item.mediaType || item.mediaType === 'text') && (
                          <h4 style={{ 
                            margin: 0, 
                            color: '#1e293b', 
                            fontSize: '1rem',
                            fontWeight: '600',
                            flex: 1
                          }}>
                            Step {item.label || item.step}
                          </h4>
                        )}
                      </div>

                      {/* Media Content */}
                      {item.mediaType === 'image' && item.mediaUrl ? (
                        <div style={{ padding: '0 1rem 1rem 1rem' }}>
                          <div style={{
                            position: 'relative',
                            borderRadius: '0.75rem',
                            overflow: 'hidden',
                            background: '#f8fafc',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                            border: '2px solid #e2e8f0'
                          }}>
                            <img
                              src={item.mediaUrl.startsWith('http') ? item.mediaUrl : `${process.env.REACT_APP_BASE_URL || 'http://localhost:3005'}${item.mediaUrl}`}
                              alt={`First aid step ${item.label || item.step}`}
                              style={{ 
                                width: '100%', 
                                height: '200px',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            {/* Fallback for broken image */}
                            <div style={{
                              display: 'none',
                              height: '200px',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: '#f3f4f6',
                              color: '#6b7280',
                              fontSize: '0.875rem'
                            }}>
                              <div style={{ textAlign: 'center' }}>
                                <ImageIcon size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                                <p style={{ margin: 0 }}>Image unavailable</p>
                              </div>
                            </div>
                            
                            {/* Image overlay with step info */}
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                              color: 'white',
                              padding: '1rem',
                              fontSize: '0.875rem',
                              fontWeight: '500'
                            }}>
                              {item.content && (
                                <p style={{ margin: 0, lineHeight: '1.4' }}>
                                  {item.content}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : item.mediaType === 'video' && item.mediaUrl ? (
                        <div style={{ padding: '0 1rem 1rem 1rem' }}>
                          <div style={{
                            position: 'relative',
                            borderRadius: '0.75rem',
                            overflow: 'hidden',
                            background: '#000',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                            border: '2px solid #e2e8f0'
                          }}>
                            <video
                              controls
                              preload="metadata"
                              src={item.mediaUrl.startsWith('http') ? item.mediaUrl : `${process.env.REACT_APP_BASE_URL || 'http://localhost:3005'}${item.mediaUrl}`}
                              style={{ 
                                width: '100%', 
                                height: '240px',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            >
                              Your browser does not support the video tag.
                            </video>
                            {/* Fallback for broken video */}
                            <div style={{
                              display: 'none',
                              height: '240px',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: '#1f2937',
                              color: '#9ca3af',
                              fontSize: '0.875rem'
                            }}>
                              <div style={{ textAlign: 'center' }}>
                                <Play size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                                <p style={{ margin: 0 }}>Video unavailable</p>
                              </div>
                            </div>
                            
                            {/* Video overlay with description */}
                            {item.content && (
                              <div style={{
                                position: 'absolute',
                                top: '0.75rem',
                                left: '0.75rem',
                                right: '0.75rem',
                                background: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                padding: '0.75rem',
                                borderRadius: '0.5rem',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                backdropFilter: 'blur(4px)'
                              }}>
                                {item.content}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        /* Text Content */
                        <div style={{ padding: '0 1rem 1rem 1rem' }}>
                          <div style={{
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            border: '1px solid #e2e8f0'
                          }}>
                            <p style={{ 
                              margin: 0, 
                              color: '#1e293b', 
                              lineHeight: '1.6', 
                              fontSize: '0.9rem',
                              fontWeight: '500'
                            }}>
                              {item.content}
                            </p>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )) : <div>No steps found for this guide.</div>}
                </div>

                {/* Emergency Notice */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  style={{
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    marginTop: '1.25rem',
                    color: 'white',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    pointerEvents: 'none'
                  }}></div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <AlertTriangle size={20} style={{ color: 'white' }} />
                    <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
                      ⚠️ IMPORTANT REMINDER
                    </h4>
                  </div>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.8rem',
                    opacity: 0.95,
                    lineHeight: '1.4',
                    color: 'white'
                  }}>
                    These are basic first aid steps. For serious emergencies or if you're unsure, 
                    seek immediate professional medical attention!
                  </p>
                </motion.div>
              </>
            ) : null}
          </div>

          {/* Compact Footer Actions */}
          <div style={{
            background: '#f8fafc',
            padding: '1rem',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <motion.button
              onClick={() => window.print()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'white',
                border: '1px solid #d1d5db',
                color: '#374151',
                padding: '0.625rem 1.25rem',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Printer size={16} />
              Print Guide
            </motion.button>
            
            <motion.button
              onClick={onClose}
              style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                border: 'none',
                color: 'white',
                padding: '0.625rem 1.5rem',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <CheckCircle size={16} />
              Got it!
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export const SummaryStep = ({
  calculatedSeverity,
  finalLinks,
  selectedVehicleDetails,
  onBackToChoice,
  socket,
  requestData,
  onEditStep,
  canEditStep
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [crewMembers, setCrewMembers] = useState({});
  const [tripStatus, setTripStatus] = useState('WAITING_FOR_CREW');
  const [isCrewComplete, setIsCrewComplete] = useState(false);
  const [acceptedDriversData, setAcceptedDriversData] = useState([]);
  const [isStartingTrip, setIsStartingTrip] = useState(false);
  const [tripStartError, setTripStartError] = useState(null);
  const [tripNotReadyMessage, setTripNotReadyMessage] = useState(null);
  const [activeCrew, setActiveCrew] = useState([]);
  const [isFirstAidModalOpen, setIsFirstAidModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  // Determine required vehicle types from selectedVehicleDetails
  const requiredTypes = selectedVehicleDetails?.isCombo
    ? selectedVehicleDetails.components.map(c => ({
        providerType: c.providerType,
        typeName: c.typeName || c.providerTypeName
      }))
    : [{
        providerType: selectedVehicleDetails?.providerType,
        typeName: selectedVehicleDetails?.typeName
      }];

  // Simulated loading delay
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Socket event listeners
  useEffect(() => {
    socket.on('crewMemberJoined', (data) => {
      console.log('Crew member joined:', data);
      const { newMember } = data;
      setCrewMembers(prev => ({
        ...prev,
        [newMember.providerType]: {
          driverId: newMember.driverId,
          name: newMember.fullName || newMember.name,
          phone: newMember.phoneNumber || newMember.phone,
          vehicleType: newMember.vehicleType
        }
      }));
    });

    socket.on('acceptanceCancelled', (data) => {
      console.log('Crew member cancelled:', data);
      const { driverId } = data;
      setCrewMembers(prev => {
        const newCrew = { ...prev };
        for (const type in newCrew) {
          if (newCrew[type].driverId === driverId) {
            delete newCrew[type];
            break;
          }
        }
        return newCrew;
      });
    });

    socket.on('emergencyTripStarted', (data) => {
      console.log('Trip started:', data);
      setTripStatus('IN_PROGRESS');
    });

    socket.on("requestAccepted", (data) => {
      setAcceptedDriversData(prevData => {
        // Check if this driver is already in the list
        const existingIndex = prevData.findIndex(item => item.driverId === data.driverId);
        
        if (existingIndex !== -1) {
          // Update existing driver data
          const updatedData = [...prevData];
          updatedData[existingIndex] = data;
          return updatedData;
        } else {
          // Add new driver data
          return [...prevData, data];
        }
      });
      
      console.log("+++++++requestAccepted++++", data);
      
      // Update crew completion status based on the response
      if (data.crewStatus?.isCrewComplete) {
        setIsCrewComplete(true);
      }
    });

    socket.on("emergencyTripStarted", (data) => {
      console.log('Emergency trip started:', data);
      setTripStatus('IN_PROGRESS');
      setIsStartingTrip(false);
      setTripStartError(null);
      setTripNotReadyMessage(null);
      if (data.crew) {
        setActiveCrew(data.crew);
      }
    });

    socket.on("emergencyTripConfirmed", (data) => {
      console.log('Trip confirmed for driver role:', data);
      setTripStatus('IN_PROGRESS');
      setIsStartingTrip(false);
    });

    socket.on("emergencyTripNotReady", (data) => {
      console.log('Trip not ready:', data);
      setTripNotReadyMessage(data.message);
      setIsStartingTrip(false);
      setTripStartError(null);
    });

    socket.on("emergencyError", (data) => {
      console.log('Emergency error:', data);
      setTripStartError(data.message);
      setIsStartingTrip(false);
      setTripNotReadyMessage(null);
    });

    return () => {
      socket.off('crewMemberJoined');
      socket.off('acceptanceCancelled');
      socket.off('emergencyTripStarted');
      socket.off('requestAccepted');
      socket.off('emergencyTripConfirmed');
      socket.off('emergencyTripNotReady');
      socket.off('emergencyError');
    };
  }, [socket]);

  // Check if all required crew members have joined
  useEffect(() => {
    const allAccepted = requiredTypes.every(type =>
      Object.keys(crewMembers).includes(String(type.providerType))
    );
    
    // Check if any of the accepted drivers has complete crew status
    const hasCompleteCrew = acceptedDriversData.some(data => data.crewStatus?.isCrewComplete);
    
    setIsCrewComplete(allAccepted || hasCompleteCrew);
  }, [crewMembers, requiredTypes, acceptedDriversData]);

  // Handle start emergency trip
  const handleStartTrip = () => {
    // Get emergencyGroupId from first accepted driver
    const emergencyGroupId = acceptedDriversData.length > 0 ? acceptedDriversData[0].emergencyGroupId : null;
    
    if (!emergencyGroupId) {
      setTripStartError('Emergency group ID not found. Please try again.');
      return;
    }

    setIsStartingTrip(true);
    setTripStartError(null);
    setTripNotReadyMessage(null);

    socket.emit('startEmergencyTrip', {
      emergencyGroupId: emergencyGroupId
    });
  };

  const renderDriverDetails = () => {
    if (acceptedDriversData.length === 0) return null;

    return (
      <>
        {acceptedDriversData.map((data, index) => {
          const { driverDetails, vehicleDetails, tripDetails } = data;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Driver Information Card */}
              <SummaryCard status="success">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                  <CheckCircle size={24} style={{ color: '#16a34a' }} />
                  <h3 style={{ margin: 0, color: '#16a34a' }}>Request Accepted!</h3>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                  {/* Driver Info */}
                  <div style={{ 
                    background: '#f8fafc', 
                    padding: '1.5rem', 
                    borderRadius: '0.75rem',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                      <User size={20} style={{ color: '#1e40af' }} />
                      <h4 style={{ margin: 0, color: '#1e293b' }}>Your Driver</h4>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: '#e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                      }}>
                        {driverDetails.photo !== '/default-avatar.png' ? (
                          <img 
                            src={driverDetails.photo} 
                            alt="Driver" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <User size={24} style={{ color: '#64748b' }} />
                        )}
                      </div>
                      
                      <div>
                        <p style={{ margin: 0, fontWeight: '600', color: '#1e293b' }}>
                          {driverDetails.fullName}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                          <Star size={16} style={{ color: '#eab308', fill: '#eab308' }} />
                          <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
                            {parseFloat(driverDetails.rating).toFixed(1)} rating
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748b' }}>
                      <Phone size={16} />
                      <span style={{ fontSize: '0.875rem' }}>{driverDetails.phoneNumber}</span>
                    </div>
                  </div>

                  {/* Vehicle Info */}
                  <div style={{ 
                    background: '#f8fafc', 
                    padding: '1.5rem', 
                    borderRadius: '0.75rem',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                      <Car size={20} style={{ color: '#1e40af' }} />
                      <h4 style={{ margin: 0, color: '#1e293b' }}>Vehicle Details</h4>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <p style={{ margin: 0, fontWeight: '600', color: '#1e293b' }}>
                        {vehicleDetails.typeName}
                      </p>
                      <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
                        {vehicleDetails.model} • {vehicleDetails.color}
                      </p>
                      <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
                        Plate: {vehicleDetails.plateNumber}
                      </p>
                    </div>
                  </div>

                  {/* ETA Info */}
                  <div style={{ 
                    background: '#f0f9ff', 
                    padding: '1.5rem', 
                    borderRadius: '0.75rem',
                    border: '1px solid #bae6fd'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                      <Clock size={20} style={{ color: '#0284c7' }} />
                      <h4 style={{ margin: 0, color: '#0c4a6e' }}>Arrival Time</h4>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold', color: '#0284c7' }}>
                        {data.estimatedArrivalTime}
                      </p>
                      <p style={{ margin: 0, color: '#0369a1', fontSize: '0.875rem' }}>
                        Estimated arrival
                      </p>
                    </div>
                  </div>
                </div>
              </SummaryCard>

              {/* Trip Details Card */}
              {tripDetails && (
                <EmergencyDetailsCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    <Route size={20} style={{ color: '#1e40af' }} />
                    <h3 style={{ margin: 0, color: '#1e40af' }}>Trip Information</h3>
                  </div>

                  <div className="route-details">
                    <div className="location">
                      <MapPin size={20} style={{ color: '#16a34a' }} />
                      <div>
                        <small>Pickup Location</small>
                        <p>Lat: {tripDetails.pickupLocation.latitude}, Lng: {tripDetails.pickupLocation.longitude}</p>
                      </div>
                    </div>
                    
                    <div className="location">
                      <Navigation size={20} style={{ color: '#dc2626' }} />
                      <div>
                        <small>Destination</small>
                        <p>Lat: {tripDetails.destination.latitude}, Lng: {tripDetails.destination.longitude}</p>
                      </div>
                    </div>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginTop: '1.5rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #e2e8f0'
                  }}>
                    <DistanceIndicator>
                      <Route size={16} />
                      <span>{tripDetails.distance} km</span>
                    </DistanceIndicator>
                    
                    {tripDetails.estimatedPrice !== "0" && (
                      <PriceTag>
                        <DollarSign size={16} />
                        <span>${tripDetails.estimatedPrice}</span>
                      </PriceTag>
                    )}
                  </div>
                </EmergencyDetailsCard>
              )}

              {/* Crew Status Card */}
              {data.crewStatus && (
                <SummaryCard>
                  <h3>Crew Status</h3>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                      {data.crewStatus.acceptedVehicleTypes.map((vehicleType, index) => (
                        <span 
                          key={index}
                          style={{
                            background: '#dcfce7',
                            color: '#166534',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                          }}
                        >
                          ✓ {vehicleType}
                        </span>
                      ))}
                    </div>
                    
                    {data.crewStatus.pendingVehicleTypes.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {data.crewStatus.pendingVehicleTypes.map((vehicleType, index) => (
                          <span 
                            key={index}
                            style={{
                              background: '#fef3c7',
                              color: '#92400e',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.875rem',
                              fontWeight: '500'
                            }}
                          >
                            ⏳ {vehicleType}
                          </span>
                        ))}
                      </div>
                    )}

                    {isCrewComplete && tripStatus !== 'IN_PROGRESS' && (
                      <div style={{
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '1rem'
                      }}>
                        <CheckCircle size={20} style={{ color: '#16a34a' }} />
                        <p style={{ margin: 0, color: '#166534', fontWeight: '500' }}>
                          All crew members are ready!
                        </p>
                      </div>
                    )}

                    {/* Trip Not Ready Message */}
                    {tripNotReadyMessage && (
                      <div style={{
                        background: '#fef3c7',
                        border: '1px solid #fcd34d',
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '1rem'
                      }}>
                        <AlertTriangle size={20} style={{ color: '#d97706' }} />
                        <p style={{ margin: 0, color: '#92400e', fontWeight: '500' }}>
                          {tripNotReadyMessage}
                        </p>
                      </div>
                    )}

                    {/* Trip Start Error */}
                    {tripStartError && (
                      <div style={{
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '1rem'
                      }}>
                        <AlertTriangle size={20} style={{ color: '#dc2626' }} />
                        <p style={{ margin: 0, color: '#991b1b', fontWeight: '500' }}>
                          {tripStartError}
                        </p>
                      </div>
                    )}

                    {/* Start Trip Button */}
                    {isCrewComplete && tripStatus !== 'IN_PROGRESS' && acceptedDriversData.length > 0 && (
                      <div style={{
                        background: 'linear-gradient(135deg, #0f766e 0%, #0891b2 100%)',
                        borderRadius: '0.75rem',
                        padding: '1.5rem',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                          pointerEvents: 'none'
                        }}></div>
                        <h4 style={{ 
                          margin: '0 0 1rem 0', 
                          color: 'white', 
                          fontSize: '1.25rem',
                          fontWeight: '600'
                        }}>
                          🚑 Ready to Dispatch Emergency Team
                        </h4>
                        <p style={{ 
                          margin: '0 0 1.5rem 0', 
                          color: 'rgba(255,255,255,0.9)', 
                          fontSize: '0.875rem'
                        }}>
                          All crew members have accepted. Click below to start the emergency response.
                        </p>
                        <Button
                          variant="primary"
                          onClick={handleStartTrip}
                          disabled={isStartingTrip}
                          style={{
                            background: 'white',
                            color: '#0f766e',
                            border: 'none',
                            padding: '0.75rem 2rem',
                            fontSize: '1rem',
                            fontWeight: '600',
                            borderRadius: '0.5rem',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            cursor: isStartingTrip ? 'not-allowed' : 'pointer',
                            opacity: isStartingTrip ? 0.7 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            justifyContent: 'center',
                            margin: '0 auto'
                          }}
                          whileHover={!isStartingTrip ? { scale: 1.05, y: -2 } : {}}
                          whileTap={!isStartingTrip ? { scale: 0.95 } : {}}
                        >
                          {isStartingTrip ? (
                            <>
                              <Loader size={20} className="spinning" />
                              Starting Trip...
                            </>
                          ) : (
                            <>
                              <Play size={20} />
                              Start Emergency Trip
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Active Trip Status */}
                    {tripStatus === 'IN_PROGRESS' && (
                      <>
                        <div style={{
                          background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
                          border: '1px solid #10b981',
                          borderRadius: '0.75rem',
                          padding: '1.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          marginBottom: '1rem',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                            pointerEvents: 'none'
                          }}></div>
                          <div style={{
                            background: 'rgba(255,255,255,0.2)',
                            borderRadius: '50%',
                            padding: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Navigation size={24} style={{ color: 'white' }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <h4 style={{ margin: 0, color: 'white', fontSize: '1.125rem', fontWeight: '600' }}>
                              🚨 Emergency Response Active
                            </h4>
                            <p style={{ margin: '0.25rem 0 0 0', color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                              Emergency team is on the way to your location
                            </p>
                          </div>
                        </div>

                        {/* Active Crew Display */}
                        {activeCrew.length > 0 && (
                          <div style={{
                            background: '#f8fafc',
                            borderRadius: '0.75rem',
                            padding: '1.5rem',
                            border: '1px solid #e2e8f0'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                              <Users size={20} style={{ color: '#1e40af' }} />
                              <h4 style={{ margin: 0, color: '#1e293b' }}>Active Emergency Crew</h4>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                              {activeCrew.map((crew, index) => (
                                <div 
                                  key={crew.driverId || index}
                                  style={{
                                    background: 'white',
                                    borderRadius: '0.5rem',
                                    padding: '1rem',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                    <div style={{
                                      background: crew.role === 'Medic Team' ? '#dcfce7' : '#dbeafe',
                                      color: crew.role === 'Medic Team' ? '#166534' : '#1e40af',
                                      padding: '0.25rem 0.5rem',
                                      borderRadius: '0.25rem',
                                      fontSize: '0.75rem',
                                      fontWeight: '500'
                                    }}>
                                      {crew.role === 'Medic Team' ? '🏥' : '🚑'} {crew.role}
                                    </div>
                                  </div>
                                  <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', color: '#1e293b' }}>
                                    {crew.driverName}
                                  </p>
                                  <p style={{ margin: '0 0 0.25rem 0', color: '#64748b', fontSize: '0.875rem' }}>
                                    {crew.vehicleType}
                                  </p>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#64748b' }}>
                                    <Phone size={14} />
                                    <span>{crew.phoneNumber}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </SummaryCard>
              )}
            </motion.div>
          );
        })}
      </>
    );
  };

  return (
    <motion.div {...animations.pageTransition}>
      <Header>
        <CheckCircle />
        <h2>Emergency Request Status</h2>
      </Header>
      <Body>
        <AnimatePresence>
          {isLoading ? (
            <ProfessionalLoader
              isVisible={isLoading}
              title="Processing Emergency Request"
              subtitle="Connecting you with emergency services and finding available ambulances..."
              showSteps={true}
              currentStep={2}
            />
          ) : error ? (
            <SummaryCard status="error">
              <AlertTriangle size={24} />
              <h3>Error</h3>
              <p>{error}</p>
              <Button variant="primary" onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </SummaryCard>
          ) : (
            <>
              {/* Two-column layout: left for summary/edit, right for driver/trip details */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '2rem',
                  alignItems: 'start',
                  marginBottom: '2rem',
                  '@media (max-width: 1024px)': {
                    gridTemplateColumns: '1fr',
                  }
                }}
              >
                {/* Left column: summary/edit cards */}
                <div>
                  <SummaryCard>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Patient Information</h3>
                      <Button
                        variant="outline"
                        onClick={() => onEditStep(1)}
                        disabled={!canEditStep || !canEditStep(1)}
                        style={{ marginLeft: 8 }}
                      >
                        📝 Edit
                      </Button>
                    </div>
                  </SummaryCard>
                  <SummaryCard>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Patient Severity Assessment</h3>
                      <Button
                        variant="outline"
                        onClick={() => onEditStep(2)}
                        disabled={!canEditStep || !canEditStep(2)}
                        style={{ marginLeft: 8 }}
                      >
                        🔄 Retake Assessment
                      </Button>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <span style={{ fontWeight: 'bold', color: '#dc2626', fontSize: '1.1rem' }}>
                        {calculatedSeverity ? `🚨 ${calculatedSeverity} Emergency` : 'Assessment Complete'}
                      </span>
                    </div>
                  </SummaryCard>
                  <SummaryCard>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Pickup & Destination</h3>
                      <Button
                        variant="outline"
                        onClick={() => onEditStep(3)}
                        disabled={!canEditStep || !canEditStep(3)}
                        style={{ marginLeft: 8 }}
                      >
                        📍 Update Location
                      </Button>
                    </div>
                  </SummaryCard>
                  <SummaryCard>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Vehicle Selection</h3>
                      <Button
                        variant="outline"
                        onClick={() => onEditStep(4)}
                        disabled={!canEditStep || !canEditStep(4)}
                        style={{ marginLeft: 8 }}
                      >
                        🚑 Change Vehicle
                      </Button>
                    </div>
                  </SummaryCard>
                  {/* First Aid Guide Button */}
                  <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                    <Button
                      variant="primary"
                      onClick={() => setIsModalOpen(true)}
                      style={{
                        background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        padding: '1rem 2rem',
                        borderRadius: '0.75rem',
                        boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.2)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        border: 'none',
                        margin: '0 auto',
                        cursor: 'pointer',
                      }}
                    >
                      <Heart size={24} />
                      Open First Aid Guide
                    </Button>
                    <FirstAidModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} guideId={finalLinks} />
                  </div>
                </div>
                {/* Right column: driver/trip details */}
                <div>
                  {renderDriverDetails()}
                </div>
              </div>
            </>
          )}
        </AnimatePresence>

        <ButtonGroup>
          <Button
            variant="primary"
            onClick={onBackToChoice}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            🏠 Back to Start
          </Button>
        </ButtonGroup>
      </Body>
    </motion.div>
  );
};